import React, { useEffect, useState, useContext, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_MENU, GET_MARKETS } from "../queries";
import buildMenuTree from "./getMenuTree";
import { LangContext } from "./LanguageProvider";
import { getFromLocalStorage, saveToLocalStorage } from "./localStorage";
import getStaticPages from "./getStaticPages";
import {
  transformString,
  capitalize,
  flattenRoutes,
  mediaDir,
  marketsDir
} from "./routeUtils";
import getComponent from "./getComponent";
import renderRoutes from "./renderRoutes";
import { parseMarkets } from "./parseMarkets";
import MarketRouteHandler from "./MarketRouteHandler";

const DynamicRoutes = () => {
  const { lang } = useContext(LangContext);
  const cacheKey = "menuData";
  const [menuTree, setMenuTree] = useState(() => {
    const cachedMenu = getFromLocalStorage(cacheKey);
    return cachedMenu ? cachedMenu : [];
  });
  const [menuItems, setMenuItems] = useState([]);

  const { loading, error, data } = useQuery(GET_MENU, {
    skip: !!getFromLocalStorage(cacheKey),
  });

  const { data: marketsData } = useQuery(GET_MARKETS);
  
  useEffect(() => {
    if (data && !loading) {
      const menus = data?.menus?.nodes;
      const menuContents = data?.menuContents?.nodes;
      let newMenuTree = buildMenuTree(menus, menuContents);
      if (marketsData && marketsData?.markets) { //validamos que marketsData y markets existan
        const parsedMarkets = parseMarkets(marketsData.markets) //parseamos y verificamos que tengan showDetailLink
          .filter(m => m.showDetailLink === 'Yes');
        
        newMenuTree = newMenuTree.map(menuObj => {
          const lang = Object.keys(menuObj)[0]; 
          const menu = menuObj[lang];
          
          const marketsMenu = menu.find(item => //verificacamos que el item sea el de markets
            item.label.toLowerCase() === marketsDir(lang)
          );

          if (marketsMenu) {
            marketsMenu.children.forEach(regionMenu => { //tomamos cada regionMenu
              const regionName = regionMenu.label.toLowerCase();
              const regionMarkets = parsedMarkets.filter(m =>
                m.region && m.region.toLowerCase() === regionName
              );
              regionMenu.children = [ 
                ...regionMenu.children,
                ...regionMarkets
                .sort((a, b) => {
                  const labelA = a.nombres[lang] || a.country;
                  const labelB = b.nombres[lang] || b.country;
                  return labelA.localeCompare(labelB);
                })
                .map(market => ({ //agregamos los markets que pertenezcan a esa region
                  label: market.nombres[lang] || market.country,
                  url: `/${lang}/${marketsDir(lang)}/${market.slug}`,
                  children: [],
                  logo: market.thumbnail ? { mediaItemUrl: market.thumbnail } : undefined,
                  description: undefined,
                }))
              ];
            })
          }
          return { [lang]: menu };
        });
            
      }
      
      setMenuTree(newMenuTree);
      saveToLocalStorage(cacheKey, newMenuTree);
    }
  }, [data, loading, marketsData]);


  const pagesContext = require.context('../pages', true, /\.js$/);

  useEffect(() => {
    const menuForLanguage = menuTree.find(item => item[lang]);
    if (menuForLanguage) {
      setMenuItems(menuForLanguage[lang]);
    }
  }, [lang, menuTree]);

  const staticPagesFlat = flattenRoutes(getStaticPages[lang]);
  const postsPaths = ["blog", "events", "press", "prensa", "eventos"];
  const marketsPaths = ["latam", "africa"];

  const memoizedRoutes = useMemo(() => {
    if (loading || error) return [];
    return renderRoutes(menuItems, lang, pagesContext, getComponent, transformString);
  }, [menuItems, lang, loading, error]);

  if (loading) return <p></p>;
  if (error) return <p></p>;

  return (
    <Router>
      <Routes>
        <Route exact path="/" element={<Navigate to={`/${lang}`} replace />} />
        <Route 
          path={`/${lang}`} 
          element={
            <React.Suspense>
              {React.createElement(React.lazy(() => import(`../pages/${capitalize(lang)}/Home.js`)))}
            </React.Suspense>
          } 
        />
        {postsPaths.map((path) => (
          <Route
            key={path}
            exact
            path={`/${lang}/${path}/*`}
            element={
              <React.Suspense>
                {React.createElement(
                  React.lazy(() =>
                    import(`../pages/${capitalize(lang)}/${capitalize(mediaDir(lang))}/PostDetail.js`)
                  )
                )}
              </React.Suspense>
            }
          />
        ))}
        {marketsPaths.map((path) => (
          <Route
            key={path}
            end
            path={`/${lang}/${marketsDir(lang)}/:param`}
            element={
              <MarketRouteHandler lang={lang}/>
            }
          />
        ))}
        {Object.entries(staticPagesFlat).map(([path, file]) => (
          <Route
            key={path}
            exact
            path={`/${lang}/${path}/*`}
            element={
              <React.Suspense>
                {React.createElement(
                  React.lazy(() =>
                    import(`../pages/${capitalize(lang)}/Statics/${file}.js`)
                  )
                )}
              </React.Suspense>
            }
          />
        ))}
        {memoizedRoutes}
        <Route path="*" element={
          <React.Suspense>
            {React.createElement(React.lazy(() => import(`../pages/NotFound.js`)))}
          </React.Suspense>} 
        />
      </Routes>
    </Router>
  );
};

export default DynamicRoutes;
